import React, {
	useState,
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";

import type {
	TAppDispatch,
	TRootState,
} from "../../redux/store";

import { ErrorIcon } from "../../assets/icons/errorIcon";
import { PencilIcon } from "../../assets/icons/pencilIcon";
import {
	AGENT_MAX_MESSAGES_PER_CHAT,
	AGENT_QUERY_USAGE_REVEAL_RATIO,
	AgentStatusMessages,
	AGENT_HISTORY_TURN_PAGE_SIZE,
	POLLING_STATUS,
} from "../../constants/agent";
import {
	isPaulTurnSuccessStatus,
	usePaulTurnRealtime,
} from "../../hooks/usePaulTurnRealtime";
import {
	addModelMessage,
	addUserMessage,
	removeMessageById,
	setPollingMessageId,
	syncConversationTurnCount,
	updateConversationTitleInState,
} from "../../redux/agent/agent.slice";
import { updateConversationTitle } from "../../redux/agent/agent.thunk";
import { services } from "../../services";
import { LoaderWithText } from "../../shared/Loader";
import {
	type TConversation,
	type TMessage,
	type TAgentProgress,
	type TDataset,
	type TConversationHistoryState,
	type TPaulInputSubmission,
} from "../../types/agent";
import { getApiErrorMessage } from "../../utils/apiErrorUtils";
import {
	createPaulInputResponsePart,
	getPaulInputAnswers,
	getPendingPaulInputQuestionIds,
} from "../../utils/paulInput";
import { toTenantPath } from "../../utils/tenantUtils";
import EditTitleModal from "../shared/EditTitleModal";

import ChatInput from "./ChatInput";
import ConversationHistoryBanner from "./ConversationHistoryBanner";
import Message from "./Message";
import PaulThinkingIndicator from "./PaulThinkingIndicator";
import UserAvatar from "./UserAvatar";

type TChatProps = {
	conversationId: string;
	selectedConversation: TConversation | null;
	messages: Array<TMessage>;
	loading: boolean;
	error: string | null;
	messageHistory: TConversationHistoryState;
	onLoadMoreMessages: () => Promise<void>;
	onRetryInitialMessages: () => Promise<void>;
	readOnly?: boolean;
};

const HISTORY_BANNER_SCROLL_FADE_DISTANCE = 72;
const TERMINAL_HISTORY_BANNER_MESSAGE_THRESHOLD = 4;
const HISTORY_SCROLL_KEYS = new Set([
	"ArrowDown",
	"ArrowUp",
	"End",
	"Home",
	"PageDown",
	"PageUp",
	" ",
]);
type TBannerStyle = React.CSSProperties &
	Record<`--${string}`, string>;

const ShareIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 18 18"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M6.75 9.75L11.25 12.375M11.25 5.625L6.75 8.25M6.75 9C6.75 10.2426 5.74264 11.25 4.5 11.25C3.25736 11.25 2.25 10.2426 2.25 9C2.25 7.75736 3.25736 6.75 4.5 6.75C5.74264 6.75 6.75 7.75736 6.75 9ZM15.75 4.875C15.75 6.11764 14.7426 7.125 13.5 7.125C12.2574 7.125 11.25 6.11764 11.25 4.875C11.25 3.63236 12.2574 2.625 13.5 2.625C14.7426 2.625 15.75 3.63236 15.75 4.875ZM15.75 13.125C15.75 14.3676 14.7426 15.375 13.5 15.375C12.2574 15.375 11.25 14.3676 11.25 13.125C11.25 11.8824 12.2574 10.875 13.5 10.875C14.7426 10.875 15.75 11.8824 15.75 13.125Z"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const PrintIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 18 18"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
	>
		<path
			d="M5.25 6.75V2.625H12.75V6.75M5.25 13.5H4.125C3.08947 13.5 2.25 12.6605 2.25 11.625V8.625C2.25 7.58947 3.08947 6.75 4.125 6.75H13.875C14.9105 6.75 15.75 7.58947 15.75 8.625V11.625C15.75 12.6605 14.9105 13.5 13.875 13.5H12.75M5.25 10.875H12.75V15.375H5.25V10.875ZM13.125 9H13.1325"
			stroke="currentColor"
			strokeWidth="1.5"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const Chat: React.FC<TChatProps> = ({
	conversationId,
	selectedConversation,
	messages,
	loading,
	error,
	messageHistory,
	onLoadMoreMessages,
	onRetryInitialMessages,
	readOnly = false,
}) => {
	const dispatch = useDispatch<TAppDispatch>();
	const scrollContainerRef = useRef<HTMLDivElement>(null);
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const hasScrolledToConversationBottomRef = useRef<
		string | null
	>(null);
	const isInitialBottomAlignmentPendingRef = useRef(false);
	const bannerVisibilityRef = useRef(0);
	const successBannerAnchorScrollTopRef = useRef<
		number | null
	>(null);
	const successBannerActiveRef = useRef(false);
	const userScrollIntentRef = useRef(false);
	const scrollListenerFrameRef = useRef<number | null>(
		null
	);
	const prependScrollSnapshotRef = useRef<{
		scrollHeight: number;
		scrollTop: number;
	} | null>(null);
	const previousLoadMoreStatusRef = useRef(
		messageHistory.loadMoreRequest.status
	);
	// The temp optimistic input-answer message id, reconciled away once the durable turn lands.
	const pendingOptimisticInputIdRef = useRef<string | null>(
		null
	);
	const isAutoLoadingMessageLinkRef = useRef(false);
	const activeConversationIdRef = useRef<string | null>(
		conversationId
	);
	const {
		updatingTitle,
		updateTitleError,
		pollingMessageId,
		agents,
		loadedConversationId,
	} = useSelector((state: TRootState) => state.agent);
	const [isEditingTitle, setIsEditingTitle] =
		useState(false);
	const [title, setTitle] = useState("");
	const [messageProgress, setMessageProgress] = useState<
		Array<TAgentProgress>
	>([]);
	const [pollingTurnIdx, setPollingTurnIdx] = useState<
		number | null
	>(null);
	const [isSubmittingInput, setIsSubmittingInput] =
		useState(false);
	const [convStatus, setConvStatus] = useState<{
		status: string;
		status_message?: string;
		turn_idx?: number;
		attempt_idx?: number;
	} | null>(null);
	const [
		historyBannerVisibility,
		setHistoryBannerVisibility,
	] = useState(0);
	const [
		isHistoryLoadSuccessBannerActive,
		setIsHistoryLoadSuccessBannerActive,
	] = useState(false);
	const totalQueriesUsed = Math.min(
		AGENT_MAX_MESSAGES_PER_CHAT,
		Math.max(
			selectedConversation?.num_turns ?? 0,
			messageHistory.totalTurns
		)
	);
	const remainingQueries = Math.max(
		0,
		AGENT_MAX_MESSAGES_PER_CHAT - totalQueriesUsed
	);
	const queryUsageRevealCount = Math.ceil(
		AGENT_MAX_MESSAGES_PER_CHAT *
			AGENT_QUERY_USAGE_REVEAL_RATIO
	);
	const isInitialMessagesLoading =
		loading ||
		(messageHistory.initialRequest.status === "loading" &&
			messages.length === 0);
	const pendingInputQuestionIds = useMemo(
		() => getPendingPaulInputQuestionIds(messages),
		[messages]
	);
	const inputAnswersByQuestionId = useMemo(
		() => getPaulInputAnswers(messages),
		[messages]
	);
	const hasPendingInput = pendingInputQuestionIds.size > 0;
	useEffect(() => {
		activeConversationIdRef.current = conversationId;

		return () => {
			if (
				activeConversationIdRef.current === conversationId
			) {
				activeConversationIdRef.current = null;
			}
		};
	}, [conversationId]);
	const scrollToBottom = useCallback(
		(behavior: ScrollBehavior = "smooth") => {
			if (scrollContainerRef.current) {
				scrollContainerRef.current.scrollTo({
					top: scrollContainerRef.current.scrollHeight,
					behavior,
				});
				return;
			}

			messagesEndRef.current?.scrollIntoView({
				behavior,
				block: "end",
			});
		},
		[]
	);

	const handleSubmitInput = useCallback(
		async ({
			response: inputResponse,
			displayValue,
		}: TPaulInputSubmission) => {
			if (
				readOnly ||
				!selectedConversation?.agent_id ||
				pollingMessageId !== null ||
				isSubmittingInput ||
				!pendingInputQuestionIds.has(
					inputResponse.question_id
				)
			) {
				return;
			}

			const optimisticMessage: TMessage = {
				msg_id: `temp-input-${Date.now()}-${Math.random()
					.toString(36)
					.slice(2, 11)}`,
				role: "user",
				turn_idx: -1,
				time_millis: Date.now(),
				content: [
					createPaulInputResponsePart(
						inputResponse.question_id,
						displayValue
					),
				],
				is_internal: false,
				attachments: null,
				refresh_sas_link_before_millis: null,
				error_msg: "",
			};

			pendingOptimisticInputIdRef.current =
				optimisticMessage.msg_id;
			setIsSubmittingInput(true);
			dispatch(addUserMessage(optimisticMessage));
			dispatch(
				setPollingMessageId(
					`pending-${optimisticMessage.msg_id}`
				)
			);
			window.setTimeout(() => {
				scrollToBottom();
			}, 100);

			try {
				const apiResponse =
					await services.agentServiceInstance.sendInputResponseToConversation(
						conversationId,
						inputResponse,
						selectedConversation.agent_id,
						{ module: "native_paul" }
					);
				if (
					activeConversationIdRef.current !== conversationId
				) {
					return;
				}

				if (typeof apiResponse.turn_idx === "number") {
					dispatch(
						syncConversationTurnCount({
							conv_id: conversationId,
							num_turns: apiResponse.turn_idx + 1,
						})
					);
				}

				if (
					apiResponse.status.status &&
					apiResponse.status.status !== "Ready"
				) {
					const modelPlaceholder: TMessage = {
						msg_id: apiResponse.msg_id,
						role: "model",
						turn_idx: apiResponse.turn_idx ?? -1,
						time_millis: Date.now(),
						content: [],
						is_internal: false,
						attachments: null,
						refresh_sas_link_before_millis: null,
						error_msg: "",
						status: POLLING_STATUS.IN_PROGRESS,
					};
					dispatch(addModelMessage(modelPlaceholder));
					dispatch(setPollingMessageId(apiResponse.msg_id));
					setPollingTurnIdx(apiResponse.turn_idx ?? null);
				} else {
					// Turn resolved synchronously, so handlePollingSuccess never runs to reconcile.
					// Keep the optimistic marker (it holds the answered state until reload), but drop
					// the ref so it can't be mistaken for a live submit later.
					dispatch(setPollingMessageId(null));
					pendingOptimisticInputIdRef.current = null;
				}
			} catch (submitError: unknown) {
				if (
					activeConversationIdRef.current !== conversationId
				) {
					return;
				}
				const errorMessage = getApiErrorMessage(
					submitError,
					"Paul could not submit your response. Please try again."
				);
				dispatch(setPollingMessageId(null));
				dispatch(
					removeMessageById(optimisticMessage.msg_id)
				);
				pendingOptimisticInputIdRef.current = null;
				toast.error(errorMessage);
			} finally {
				if (
					activeConversationIdRef.current === conversationId
				) {
					setIsSubmittingInput(false);
				}
			}
		},
		[
			conversationId,
			dispatch,
			isSubmittingInput,
			pendingInputQuestionIds,
			pollingMessageId,
			readOnly,
			scrollToBottom,
			selectedConversation,
		]
	);

	const setBannerVisibilityValue = useCallback(
		(visibility: number) => {
			const nextVisibility = Math.max(
				0,
				Math.min(1, visibility)
			);

			if (
				Math.abs(
					bannerVisibilityRef.current - nextVisibility
				) < 0.01
			) {
				return;
			}

			bannerVisibilityRef.current = nextVisibility;
			setHistoryBannerVisibility(nextVisibility);
		},
		[]
	);

	const setHistoryLoadSuccessBannerActiveValue =
		useCallback((active: boolean) => {
			if (successBannerActiveRef.current === active) {
				return;
			}

			successBannerActiveRef.current = active;
			setIsHistoryLoadSuccessBannerActive(active);
		}, []);

	const updateHistoryBannerVisibility = useCallback(() => {
		const scrollContainer = scrollContainerRef.current;
		if (
			!scrollContainer ||
			messageHistory.totalTurns === 0 ||
			isInitialBottomAlignmentPendingRef.current
		) {
			successBannerAnchorScrollTopRef.current = null;
			setHistoryLoadSuccessBannerActiveValue(false);
			setBannerVisibilityValue(0);
			return;
		}

		if (successBannerAnchorScrollTopRef.current !== null) {
			if (!userScrollIntentRef.current) {
				setHistoryLoadSuccessBannerActiveValue(true);
				setBannerVisibilityValue(1);
				return;
			}

			userScrollIntentRef.current = false;
			successBannerAnchorScrollTopRef.current = null;
			setHistoryLoadSuccessBannerActiveValue(false);
		}

		setHistoryLoadSuccessBannerActiveValue(false);
		setBannerVisibilityValue(
			1 -
				scrollContainer.scrollTop /
					HISTORY_BANNER_SCROLL_FADE_DISTANCE
		);
	}, [
		messageHistory.totalTurns,
		setBannerVisibilityValue,
		setHistoryLoadSuccessBannerActiveValue,
	]);

	const getPollingErrorMessage = (
		pollingError: unknown
	): string => {
		if (
			typeof pollingError === "object" &&
			pollingError !== null
		) {
			const error = pollingError as {
				data?: { msg?: unknown };
				message?: unknown;
				statusText?: unknown;
			};

			if (
				typeof error.data?.msg === "string" &&
				error.data.msg.trim().length > 0
			) {
				return error.data.msg;
			}

			if (
				typeof error.message === "string" &&
				error.message.trim().length > 0
			) {
				return error.message;
			}

			if (
				typeof error.statusText === "string" &&
				error.statusText.trim().length > 0
			) {
				return error.statusText;
			}
		}

		return "Paul encountered an error while processing this request.";
	};

	const handlePollingSuccess = (
		data: TMessage,
		convStatusData?: {
			status: string;
			status_message?: string;
			turn_idx?: number;
			attempt_idx?: number;
		},
		turnMessages?: Array<TMessage>
	) => {
		setMessageProgress(data.progress_logs ?? []);
		setConvStatus(convStatusData ?? null);

		// Only stop polling when conversation is Ready.
		// This ensures we wait for all retry attempts to complete.
		// When status is Ready, the message will contain either:
		// - Success: content with output data
		// - Failure: error_msg with the final error (after all retries exhausted)
		const isConvReady = isPaulTurnSuccessStatus(
			convStatusData?.status
		);
		// Only treat successful content as terminal when status isn't Ready.
		// Don't stop on error_msg alone - the server may still be retrying.
		const hasSuccessPayload = data.content.length > 0;

		// Some deployments can return a stale in-progress status briefly after the
		// final payload is already persisted. Stop polling if we have success content.
		if (isConvReady || hasSuccessPayload) {
			// Persist the whole turn (durable user input-answer marker + model message) so the
			// answered picker survives; both add-actions dedupe on msg_id+role. Fall back to the
			// model message alone if the turn payload is absent.
			const durableMessages =
				turnMessages && turnMessages.length > 0
					? turnMessages
					: [data];
			durableMessages.forEach((message) =>
				dispatch(
					message.role === "user"
						? addUserMessage(message)
						: addModelMessage(message)
				)
			);
			if (pendingOptimisticInputIdRef.current) {
				dispatch(
					removeMessageById(
						pendingOptimisticInputIdRef.current
					)
				);
				pendingOptimisticInputIdRef.current = null;
			}
			setPollingTurnIdx(null);
			dispatch(setPollingMessageId(null));
			setConvStatus(null);
		}
	};

	const handlePollingError = (error: unknown) => {
		console.error("Realtime turn failed", error);
		const errorMsg = getPollingErrorMessage(error);
		const activePollingMessageId = pollingMessageId;
		if (activePollingMessageId) {
			const placeholderMessage = messages.find(
				(msg) =>
					msg.msg_id === activePollingMessageId &&
					msg.role === "model"
			);
			if (placeholderMessage) {
				dispatch(
					addModelMessage({
						...placeholderMessage,
						status: POLLING_STATUS.ERROR,
						error_msg: errorMsg,
					})
				);
			} else {
				dispatch(
					addModelMessage({
						msg_id: `poll-error-${Date.now()}`,
						role: "model",
						turn_idx: pollingTurnIdx ?? -1,
						time_millis: Date.now(),
						content: [],
						is_internal: false,
						attachments: null,
						refresh_sas_link_before_millis: null,
						error_msg: errorMsg,
						status: POLLING_STATUS.ERROR,
					})
				);
			}
		}

		toast.error(errorMsg);
		setConvStatus(null);
		setPollingTurnIdx(null);
		dispatch(setPollingMessageId(null));
	};
	usePaulTurnRealtime({
		conversationId,
		turnIdx: pollingTurnIdx ?? 0,
		onSuccess: handlePollingSuccess,
		onStatus: setConvStatus,
		onError: handlePollingError,
		agentService: services.agentServiceInstance,
		realtimeService: services.realtimeServiceInstance,
		enabled: pollingTurnIdx !== null,
		maxWaitTime: 30 * 60 * 1000, // 30 minutes
	});

	useEffect(() => {
		dispatch(setPollingMessageId(null));
		setPollingTurnIdx(null);
		hasScrolledToConversationBottomRef.current = null;
		isInitialBottomAlignmentPendingRef.current = false;
		prependScrollSnapshotRef.current = null;
		successBannerAnchorScrollTopRef.current = null;
		userScrollIntentRef.current = false;
		setHistoryLoadSuccessBannerActiveValue(false);
		setBannerVisibilityValue(0);
		previousLoadMoreStatusRef.current = "idle";
	}, [
		conversationId,
		dispatch,
		setBannerVisibilityValue,
		setHistoryLoadSuccessBannerActiveValue,
	]);

	useLayoutEffect(() => {
		const previousStatus =
			previousLoadMoreStatusRef.current;
		const currentStatus =
			messageHistory.loadMoreRequest.status;
		const scrollContainer = scrollContainerRef.current;

		if (
			previousStatus === "loading" &&
			currentStatus === "success" &&
			prependScrollSnapshotRef.current &&
			scrollContainer
		) {
			const { scrollHeight, scrollTop } =
				prependScrollSnapshotRef.current;
			const heightDelta =
				scrollContainer.scrollHeight - scrollHeight;
			scrollContainer.scrollTop = scrollTop + heightDelta;
			successBannerAnchorScrollTopRef.current =
				scrollContainer.scrollTop;
			setHistoryLoadSuccessBannerActiveValue(true);
			prependScrollSnapshotRef.current = null;
			updateHistoryBannerVisibility();
		}

		if (
			previousStatus === "loading" &&
			currentStatus !== "loading" &&
			currentStatus !== "success"
		) {
			prependScrollSnapshotRef.current = null;
			successBannerAnchorScrollTopRef.current = null;
			setHistoryLoadSuccessBannerActiveValue(false);
		}

		previousLoadMoreStatusRef.current = currentStatus;
	}, [
		messageHistory.loadMoreRequest.status,
		messages.length,
		setHistoryLoadSuccessBannerActiveValue,
		updateHistoryBannerVisibility,
	]);

	useEffect(() => {
		const scrollContainer = scrollContainerRef.current;
		if (!scrollContainer) {
			setBannerVisibilityValue(0);
			return;
		}

		const handleScroll = () => {
			if (scrollListenerFrameRef.current !== null) {
				return;
			}

			scrollListenerFrameRef.current =
				window.requestAnimationFrame(() => {
					scrollListenerFrameRef.current = null;
					updateHistoryBannerVisibility();
				});
		};
		const markUserScrollIntent = () => {
			userScrollIntentRef.current = true;
		};
		const handleKeyDown = (event: KeyboardEvent) => {
			const target = event.target;
			if (
				target instanceof HTMLElement &&
				["BUTTON", "INPUT", "SELECT", "TEXTAREA"].includes(
					target.tagName
				)
			) {
				return;
			}

			if (HISTORY_SCROLL_KEYS.has(event.key)) {
				markUserScrollIntent();
			}
		};

		handleScroll();
		scrollContainer.addEventListener(
			"scroll",
			handleScroll,
			{
				passive: true,
			}
		);
		scrollContainer.addEventListener(
			"wheel",
			markUserScrollIntent,
			{ passive: true }
		);
		scrollContainer.addEventListener(
			"touchmove",
			markUserScrollIntent,
			{ passive: true }
		);
		scrollContainer.addEventListener(
			"keydown",
			handleKeyDown
		);

		return () => {
			if (scrollListenerFrameRef.current !== null) {
				window.cancelAnimationFrame(
					scrollListenerFrameRef.current
				);
				scrollListenerFrameRef.current = null;
			}
			scrollContainer.removeEventListener(
				"scroll",
				handleScroll
			);
			scrollContainer.removeEventListener(
				"wheel",
				markUserScrollIntent
			);
			scrollContainer.removeEventListener(
				"touchmove",
				markUserScrollIntent
			);
			scrollContainer.removeEventListener(
				"keydown",
				handleKeyDown
			);
		};
	}, [
		messages.length,
		setBannerVisibilityValue,
		updateHistoryBannerVisibility,
	]);

	useEffect(() => {
		if (selectedConversation) {
			setTitle(selectedConversation.title);
			// Check if conversation is in progress and start polling
			if (
				selectedConversation.status &&
				selectedConversation.status.status !== "Ready"
			) {
				const turnIdx =
					selectedConversation.status.turn_idx;
				if (turnIdx !== undefined) {
					setPollingTurnIdx(turnIdx);
					// Create a temporary msg_id for polling tracking
					const tempMsgId = `polling-${conversationId}-${turnIdx}`;
					dispatch(setPollingMessageId(tempMsgId));
					setConvStatus(selectedConversation.status);
				}
			}
		}
	}, [selectedConversation, conversationId, dispatch]);

	useEffect(() => {
		if (messages.length === 0) {
			return;
		}

		const lastMessage = messages[messages.length - 1];
		if (
			lastMessage.status === POLLING_STATUS.IN_PROGRESS ||
			lastMessage.status === POLLING_STATUS.NOT_YET_STARTED
		) {
			dispatch(setPollingMessageId(lastMessage.msg_id));
			const turnIdx =
				lastMessage.turn_idx >= 0
					? lastMessage.turn_idx
					: Math.floor((messages.length - 1) / 2);
			setPollingTurnIdx(turnIdx);
		}
	}, [messages, dispatch]);

	useLayoutEffect(() => {
		if (!pollingMessageId) {
			return;
		}

		userScrollIntentRef.current = false;

		const scrollPendingStatusIntoView = () => {
			if (scrollContainerRef.current) {
				scrollContainerRef.current.scrollTo({
					top: scrollContainerRef.current.scrollHeight,
					behavior: "auto",
				});
				return;
			}

			messagesEndRef.current?.scrollIntoView({
				behavior: "auto",
				block: "end",
			});
		};

		scrollPendingStatusIntoView();

		const animationFrameId = window.requestAnimationFrame(
			scrollPendingStatusIntoView
		);
		const settleTimeoutId = window.setTimeout(
			scrollPendingStatusIntoView,
			120
		);

		return () => {
			window.cancelAnimationFrame(animationFrameId);
			window.clearTimeout(settleTimeoutId);
		};
	}, [pollingMessageId]);

	const location = useLocation();

	useEffect(() => {
		const searchParams = new URLSearchParams(
			location.search
		);
		const messageId = searchParams.get("messageId");

		if (messageId && messages.length > 0) {
			// Retry scrolling for a few seconds to handle async rendering/layout shifts
			let attempts = 0;
			const maxAttempts = 20; // 20 * 250ms = 5 seconds
			const intervalId = setInterval(() => {
				const element = document.getElementById(messageId);
				if (element) {
					element.scrollIntoView({
						behavior: "smooth",
						block: "start",
					});

					element.classList.add("highlight-message");
					setTimeout(() => {
						element.classList.remove("highlight-message");
					}, 2000);
					clearInterval(intervalId);
				}

				attempts++;
				if (attempts >= maxAttempts) {
					clearInterval(intervalId);
				}
			}, 500);

			return () => {
				clearInterval(intervalId);
			};
		}
	}, [messages, location.search, loading]);

	useEffect(() => {
		if (location.search.includes("messageId=")) {
			return;
		}

		const isCurrentConversationLoaded =
			loadedConversationId === conversationId;
		const hasFinishedInitialLoad =
			messageHistory.initialRequest.status === "success" ||
			messageHistory.initialRequest.status === "empty";

		if (
			!isCurrentConversationLoaded ||
			!hasFinishedInitialLoad ||
			(messages.length === 0 &&
				messageHistory.initialRequest.status !== "empty") ||
			hasScrolledToConversationBottomRef.current ===
				conversationId
		) {
			return;
		}

		hasScrolledToConversationBottomRef.current =
			conversationId;
		isInitialBottomAlignmentPendingRef.current = true;
		setBannerVisibilityValue(0);

		const alignToBottom = () => {
			scrollToBottom("auto");
		};

		let secondAnimationFrameId: number | null = null;
		const animationFrameId = window.requestAnimationFrame(
			() => {
				alignToBottom();
				secondAnimationFrameId =
					window.requestAnimationFrame(() => {
						alignToBottom();
					});
			}
		);
		const settleTimeoutId = window.setTimeout(() => {
			alignToBottom();
			isInitialBottomAlignmentPendingRef.current = false;
			updateHistoryBannerVisibility();
		}, 140);

		return () => {
			window.cancelAnimationFrame(animationFrameId);
			if (secondAnimationFrameId !== null) {
				window.cancelAnimationFrame(secondAnimationFrameId);
			}
			window.clearTimeout(settleTimeoutId);
			isInitialBottomAlignmentPendingRef.current = false;
		};
	}, [
		conversationId,
		loadedConversationId,
		location.search,
		messageHistory.initialRequest.status,
		messages.length,
		scrollToBottom,
		setBannerVisibilityValue,
		updateHistoryBannerVisibility,
	]);

	const requestMoreHistory = useCallback(
		async (options?: { preserveScroll?: boolean }) => {
			if (
				messageHistory.loadMoreRequest.status ===
					"loading" ||
				!messageHistory.hasOlderTurns
			) {
				return;
			}

			successBannerAnchorScrollTopRef.current = null;
			userScrollIntentRef.current = false;
			setHistoryLoadSuccessBannerActiveValue(false);

			if (
				options?.preserveScroll !== false &&
				scrollContainerRef.current
			) {
				prependScrollSnapshotRef.current = {
					scrollHeight:
						scrollContainerRef.current.scrollHeight,
					scrollTop: scrollContainerRef.current.scrollTop,
				};
			} else {
				prependScrollSnapshotRef.current = null;
			}

			await onLoadMoreMessages();
		},
		[
			messageHistory.hasOlderTurns,
			messageHistory.loadMoreRequest.status,
			onLoadMoreMessages,
			setHistoryLoadSuccessBannerActiveValue,
		]
	);

	useEffect(() => {
		const searchParams = new URLSearchParams(
			location.search
		);
		const messageId = searchParams.get("messageId");

		if (
			!messageId ||
			messages.some(
				(message) => message.msg_id === messageId
			) ||
			messageHistory.initialRequest.status === "loading" ||
			messageHistory.loadMoreRequest.status === "loading" ||
			!messageHistory.hasOlderTurns ||
			isAutoLoadingMessageLinkRef.current
		) {
			return;
		}

		isAutoLoadingMessageLinkRef.current = true;
		void requestMoreHistory({
			preserveScroll: false,
		}).finally(() => {
			isAutoLoadingMessageLinkRef.current = false;
		});
	}, [
		location.search,
		messageHistory.hasOlderTurns,
		messageHistory.initialRequest.status,
		messageHistory.loadMoreRequest.status,
		messages,
		requestMoreHistory,
	]);

	const getDatasetName = () => {
		const agentId = selectedConversation?.agent_id;
		const datasetId =
			selectedConversation?.agent_dataset_id;

		if (!agentId || !datasetId) {
			return "";
		}
		if (!(agentId in agents)) {
			return "";
		}

		const agent = agents[agentId];
		const dataset = agent.datasets.find(
			(ds: TDataset) => ds.id === datasetId
		);
		return dataset?.name ?? datasetId;
	};

	const getAgentName = () => {
		const agentId = selectedConversation?.agent_id;
		if (!agentId) {
			return "";
		}
		if (!(agentId in agents)) {
			return "";
		}
		return agents[agentId].name;
	};

	const getLatestStatusOfTheAgent = () => {
		// If we have conv_status and it's not Ready, show it with attempt number
		if (
			convStatus?.status &&
			convStatus.status !== "Ready"
		) {
			const attemptNum =
				convStatus.attempt_idx !== undefined
					? convStatus.attempt_idx + 1
					: 0;
			const statusMsg =
				convStatus.status_message ?? convStatus.status;
			return `${statusMsg}${attemptNum > 0 ? ` (att. ${attemptNum})` : ""}`;
		}
		// Fallback to message progress logs
		if (messageProgress.length > 0) {
			const lastProgress =
				messageProgress[messageProgress.length - 1];
			return AgentStatusMessages[lastProgress.status];
		}
		return "Paul is processing your request.";
	};

	const getLatestPaulActivityStatus = () => {
		if (
			convStatus?.status &&
			convStatus.status !== "Ready"
		) {
			return convStatus.status;
		}

		return (
			messageProgress[messageProgress.length - 1]?.status ??
			POLLING_STATUS.NOT_YET_STARTED
		);
	};

	const handleSaveTitle = () => {
		if (
			selectedConversation &&
			title !== selectedConversation.title
		) {
			void dispatch(
				updateConversationTitle({
					conversationId: selectedConversation.conv_id,
					newTitle: title,
				})
			)
				.unwrap()
				.then(() => {
					dispatch(
						updateConversationTitleInState({
							conv_id: selectedConversation.conv_id,
							title,
						})
					);
					setIsEditingTitle(false);
				})
				.catch(() => {
					/* Error already handled by Redux */
				});
		} else {
			setIsEditingTitle(false);
		}
	};

	const handleCopyShareLink = () => {
		const url = `${window.location.origin}${toTenantPath(
			`/agent/chat/${conversationId}`
		)}`;

		void navigator.clipboard
			.writeText(url)
			.then(() => {
				toast.success("Share link copied to clipboard!");
			})
			.catch(() => {
				toast.error("Couldn't copy the share link.");
			});
	};

	const handlePrint = () => {
		window.print();
	};

	const renderContent = () => {
		const initialLoadError =
			messageHistory.initialRequest.error ?? error;

		if (isInitialMessagesLoading) {
			return (
				<div className="chat-status-container">
					<LoaderWithText
						withText={true}
						text="Loading the latest messages..."
						loaderWidth="30"
					/>
				</div>
			);
		}

		if (
			messages.length === 0 &&
			(initialLoadError !== null ||
				messageHistory.initialRequest.status === "error")
		) {
			return (
				<div className="chat-status-container">
					<div className="sidebar-error sidebar-error--actionable">
						<ErrorIcon />
						<div>
							<p>Error loading messages.</p>
							{initialLoadError && (
								<span>{initialLoadError}</span>
							)}
						</div>
						<button
							type="button"
							className="conversation-load-retry-button"
							onClick={() => {
								void onRetryInitialMessages();
							}}
						>
							Retry
						</button>
					</div>
				</div>
			);
		}

		// Only show "No messages yet" if:
		// 1. No messages exist
		// 2. Not currently polling for a message
		// 3. Not in loading state
		// 4. We have loaded the conversation metadata AND it has 0 turns (truly empty)
		if (
			messages.length === 0 &&
			!pollingMessageId &&
			(messageHistory.initialRequest.status === "empty" ||
				selectedConversation?.num_turns === 0)
		) {
			return (
				<div className="chat-status-container">
					<div className="chat-empty-state">
						<p className="chat-empty-state__title">
							No messages yet.
						</p>
						<p className="chat-empty-state__body">
							Start the conversation and Paul will reply
							here.
						</p>
					</div>
				</div>
			);
		}

		const filteredMessages = messages.filter((msg) => {
			return msg.status !== POLLING_STATUS.IN_PROGRESS;
		});

		const lastAIMessageWithCode = [...messages]
			.reverse()
			.find(
				(msg) => msg.role === "model" && msg.raw_content
			);
		const oldestLoadedTurnIdx =
			messageHistory.loadedTurnIndices.length > 0
				? Math.min(...messageHistory.loadedTurnIndices)
				: messageHistory.totalTurns;
		const hiddenHistoryCount = messageHistory.hasOlderTurns
			? Math.max(0, oldestLoadedTurnIdx)
			: 0;
		const nextHistoryLoadCount =
			messageHistory.hasOlderTurns
				? Math.min(
						AGENT_HISTORY_TURN_PAGE_SIZE,
						Math.max(0, oldestLoadedTurnIdx)
					)
				: 0;
		const bannerStatus =
			isHistoryLoadSuccessBannerActive &&
			messageHistory.loadMoreRequest.status === "success"
				? "success"
				: messageHistory.loadMoreRequest.status ===
					  "success"
					? "idle"
					: messageHistory.loadMoreRequest.status;
		const isTerminalHistoryBanner =
			!messageHistory.hasOlderTurns &&
			bannerStatus !== "loading" &&
			bannerStatus !== "error" &&
			bannerStatus !== "success";
		const shouldShowHistoryBanner =
			messageHistory.totalTurns > 0 &&
			(!isTerminalHistoryBanner ||
				filteredMessages.length >
					TERMINAL_HISTORY_BANNER_MESSAGE_THRESHOLD);
		const isHistoryBannerVisible =
			historyBannerVisibility > 0.04;
		const historyBannerStyle: TBannerStyle = {
			"--conversation-history-banner-opacity": `${historyBannerVisibility}`,
			"--conversation-history-banner-offset": `${(1 - historyBannerVisibility) * -16}px`,
			"--conversation-history-banner-scale": `${0.985 + historyBannerVisibility * 0.015}`,
		};

		return (
			<div
				className="chat-messages"
				ref={scrollContainerRef}
			>
				<div className="chat-messages-content">
					{shouldShowHistoryBanner && (
						<div
							className={`conversation-history-banner-shell ${
								isHistoryBannerVisible
									? "is-visible"
									: "is-hidden"
							} conversation-history-banner-shell--${bannerStatus}`}
							style={historyBannerStyle}
						>
							<ConversationHistoryBanner
								hasOlderTurns={messageHistory.hasOlderTurns}
								status={bannerStatus}
								hiddenCount={hiddenHistoryCount}
								nextLoadCount={nextHistoryLoadCount}
								lastLoadedCount={
									bannerStatus === "success"
										? messageHistory.loadMoreRequest
												.lastLoadedCount
										: 0
								}
								onLoadMore={() => {
									void requestMoreHistory();
								}}
								onJumpToLatest={() => {
									scrollToBottom("auto");
								}}
							/>
						</div>
					)}
					{filteredMessages.map((msg) => (
						<Message
							key={`${msg.msg_id}-${msg.role}`}
							message={msg}
							isLastAIMessageWithCode={
								msg.msg_id === lastAIMessageWithCode?.msg_id
							}
							readOnly={readOnly}
							pendingInputQuestionIds={
								pendingInputQuestionIds
							}
							inputAnswersByQuestionId={
								inputAnswersByQuestionId
							}
							inputsDisabled={
								Boolean(pollingMessageId) ||
								isSubmittingInput
							}
							onSubmitInput={handleSubmitInput}
						/>
					))}
					{pollingMessageId && (
						<div className="chat-status-container">
							<PaulThinkingIndicator
								variant="hero"
								status={getLatestPaulActivityStatus()}
								message={getLatestStatusOfTheAgent()}
							/>
						</div>
					)}
					<div ref={messagesEndRef} />
				</div>
			</div>
		);
	};

	return (
		<div className="chat-container">
			<div className="chat-content">
				<div className="chat-header">
					<div className="chat-header-left">
						<div className="chat-header-copy">
							<div className="title-container">
								<h1 className="chat-title">
									{selectedConversation?.title}
								</h1>
								{selectedConversation?.is_owner &&
									!readOnly && (
										<button
											className="edit-title-button"
											onClick={() => {
												setIsEditingTitle(true);
											}}
										>
											<PencilIcon />
										</button>
									)}
							</div>
							<div className="chat-header-meta">
								{getAgentName() !== "" && (
									<div className="dataset-info">
										<span>{getAgentName()}</span>
									</div>
								)}
								{getDatasetName() !== "" && (
									<div className="dataset-info">
										<span>{getDatasetName()}</span>
									</div>
								)}
								{selectedConversation?.user_name && (
									<div className="user-info">
										<UserAvatar
											userName={
												selectedConversation.user_name
											}
											size={28}
											fontSize={12}
										/>
									</div>
								)}
							</div>
						</div>
					</div>
					<div className="chat-header-right">
						<div className="chat-header-actions">
							<button
								type="button"
								className="chat-header-icon-button"
								aria-label="Copy share link"
								title="Copy share link"
								onClick={handleCopyShareLink}
							>
								<ShareIcon />
							</button>
							<button
								type="button"
								className="chat-header-icon-button"
								aria-label="Print conversation"
								title="Print conversation"
								onClick={handlePrint}
							>
								<PrintIcon />
							</button>
						</div>
					</div>
				</div>
				{renderContent()}
				<ChatInput
					conversationId={conversationId}
					selectedConversation={selectedConversation}
					maxQueries={AGENT_MAX_MESSAGES_PER_CHAT}
					remainingQueries={remainingQueries}
					usedQueries={totalQueriesUsed}
					isUsageLoading={isInitialMessagesLoading}
					queryUsageRevealCount={queryUsageRevealCount}
					scrollToBottom={() => {
						scrollToBottom();
					}}
					readOnly={readOnly}
					inputLocked={hasPendingInput}
					paulStatus={getLatestPaulActivityStatus()}
				/>
			</div>
			<EditTitleModal
				open={isEditingTitle}
				onClose={() => {
					setIsEditingTitle(false);
				}}
				title={title}
				setTitle={setTitle}
				onSave={handleSaveTitle}
				loading={updatingTitle}
				error={updateTitleError}
			/>
		</div>
	);
};

export default Chat;
